/**
 * Meta Pixel Manager
 * Handles Facebook Pixel integration using shared consent
 */

import type {
  FacebookStandardEvent,
  AnalyticsEventData,
  ConsentManager,
} from '../types/analytics.types';
import type { StatsManager } from '../core/stats-manager';
import type { Config } from '../types/config.types';
import type { EventName, EventData, EventMetadata } from '../types/events.types';
import { ConsentGatedPixel, NON_FORWARDED_EVENTS } from './consent-gated-pixel';

/**
 * Dependencies for MetaPixelManager
 */
interface MetaPixelDeps {
  consentManager: ConsentManager;
  statsManager: StatsManager;
  config?: Config;
}

/**
 * Meta Pixel Manager
 */
export class MetaPixelManager extends ConsentGatedPixel<MetaPixelDeps> {
  private pixelId = '847770357286611';

  /**
   * Initialize Meta Pixel Manager with dependencies
   */
  init(deps: Partial<MetaPixelDeps>): void {
    this.requireDeps(deps, ['consentManager', 'statsManager'], 'MetaPixelManager');

    console.log('Meta Pixel Manager initializing...');

    // Meta's recommended GDPR pattern: in strict-consent regions (EU/EEA/UK/CH) call
    // fbq('consent','revoke') BEFORE init, so fbevents.js holds every event and sets no
    // cookies until the user grants. Non-strict regions (e.g. US) auto-grant immediately via
    // onConsentGranted below — no revoke, clean immediate tracking.
    if (!this.deps.consentManager!.hasConsent() && typeof window.fbq === 'function') {
      window.fbq('consent', 'revoke');
      console.log('Meta Pixel: consent revoked until granted (strict-consent region)');
    }

    // Initialize the vendor + subscribe once full consent is granted (immediate where not required).
    this.initOnFullConsent();
  }

  /**
   * Initialize Facebook Pixel with consent
   */
  protected initializePixel(): void {
    if (this.initialized) {
      return;
    }

    // Check consent status
    if (this.deps.consentManager && !this.deps.consentManager.hasConsent()) {
      console.log('Meta pixel not initialized - consent not given');
      return;
    }

    try {
      // Check if fbq is available
      if (typeof window.fbq === 'undefined') {
        console.error('Meta Pixel not loaded');
        return;
      }

      // Initialize pixel with user consent
      window.fbq('consent', 'grant');
      window.fbq('init', this.pixelId);
      window.fbq('track', 'PageView');

      this.initialized = true;
      console.log('Meta Pixel initialized:', this.pixelId);
    } catch (error) {
      console.error('Failed to initialize Meta Pixel:', error);
    }
  }

  /**
   * Track custom event
   */
  trackEvent(eventName: string, parameters: AnalyticsEventData = {}): void {
    if (!this.initialized) {
      console.log('Meta Pixel not initialized');
      return;
    }

    try {
      // Add common metadata
      const enrichedParams = {
        ...parameters,
        timestamp: new Date().toISOString(),
        environment: this.deps.config?.environment || 'unknown',
      };

      window.fbq!('trackCustom', eventName, enrichedParams);
      console.log('Meta Pixel event:', eventName, enrichedParams);
    } catch (error) {
      console.error('Failed to track Meta Pixel event:', error);
    }
  }

  /**
   * Track standard Facebook event
   */
  trackStandardEvent(eventName: FacebookStandardEvent, parameters: AnalyticsEventData = {}): void {
    if (!this.initialized) {
      console.log('Meta Pixel not initialized');
      return;
    }

    try {
      window.fbq!('track', eventName, parameters);
      console.log('Meta Pixel standard event:', eventName, parameters);
    } catch (error) {
      console.error('Failed to track Meta Pixel standard event:', error);
    }
  }

  /**
   * Handle events from StatsManager
   */
  protected handleStatsManagerEvent(
    eventName: EventName,
    eventData: EventData,
    _eventMetadata: EventMetadata,
    _value: string | number | null
  ): void {
    if (!this.initialized) {
      // Silently skip if not initialized (waiting for consent)
      return;
    }

    try {
      const data = eventData as AnalyticsEventData;

      // Map generic events to Meta Pixel events
      switch (eventName) {
        case 'screen_viewed':
          this.trackScreenView(data.screen as string, data.screenHash as string);
          break;
        case 'email_captured':
          this.trackEmailCaptured();
          break;
        case 'funnel_step_completed':
          this.trackFunnelStepCompleted(data.step as string, data);
          break;
        case 'paywall_viewed':
          this.trackPaywallViewed(data);
          break;
        case 'checkout_initiated':
          this.trackCheckoutInitiated(data);
          break;
        case 'subscription_completed':
          this.trackPurchaseCompleted(data);
          break;
        default:
          // Track other events as custom events
          if (!NON_FORWARDED_EVENTS.has(eventName)) {
            this.trackEvent(eventName, data);
          }
      }
    } catch (error) {
      console.error('Meta Pixel: Failed to handle event:', eventName, error);
    }
  }

  /**
   * Track screen viewed
   */
  private trackScreenView(screenName: string, screenHash: string): void {
    this.trackEvent('ScreenViewed', {
      screen_name: screenName,
      screen_hash: screenHash,
    });
  }

  /**
   * Track email capture
   */
  private trackEmailCaptured(): void {
    // NOTE: the email is intentionally NOT sent to Meta. Advanced Matching (passing the
    // hashed email to fbq) would improve match quality but needs its own consent review.
    this.trackStandardEvent('Lead', {
      content_name: 'email_capture',
      content_category: 'lead_generation',
    });

    this.trackEvent('EmailCaptured', {});
  }

  /**
   * Track funnel step completed
   */
  private trackFunnelStepCompleted(step: string, data: AnalyticsEventData = {}): void {
    this.trackEvent('FunnelStepCompleted', {
      step,
      ...data,
    });
  }

  /**
   * Track paywall viewed
   */
  private trackPaywallViewed(data: AnalyticsEventData = {}): void {
    this.trackStandardEvent('ViewContent', {
      content_name: 'subscription_paywall',
      content_type: 'product',
      value: data.price || 0,
      currency: data.currency || 'USD',
    });

    this.trackEvent('PaywallViewed', data);
  }

  /**
   * Track checkout initiated
   */
  private trackCheckoutInitiated(data: AnalyticsEventData = {}): void {
    this.trackStandardEvent('InitiateCheckout', {
      content_name: 'vocalist_subscription',
      content_category: 'subscription',
      value: data.price || 0,
      currency: data.currency || 'USD',
    });

    this.trackEvent('CheckoutInitiated', data);
  }

  /**
   * Track purchase/subscription completed
   */
  private trackPurchaseCompleted(data: AnalyticsEventData = {}): void {
    this.trackStandardEvent('Purchase', {
      content_name: 'vocalist_subscription',
      content_type: 'subscription',
      value: (data.amount as number) || 0, // already in major currency units (dollars)
      currency: data.currency || 'USD',
      transaction_id: data.transactionId,
    });

    this.trackEvent('SubscriptionCompleted', data);
  }
}

// Create singleton instance
export const metaPixelManager = new MetaPixelManager();
